/**
 * PartnerPage — /partner (PrivateRoute)
 *
 * Mirrors prizes PartnerPage pattern:
 * 1. Hero card — pitch + referral share if approved
 * 2. Progress bar — if pending or approved
 * 3. Application form — if status === 'none' or 'rejected'
 * 4. Pending state card
 * 5. Approved state — badge, referral code, earnings, instructions
 *
 * API: GET /users/me/partner, POST /users/me/partner-application, GET /users/me/loyalty
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  getPartnerStatus,
  applyForPartner,
  getLoyalty,
  type PartnerStatusResponse,
  type LoyaltyResponse,
  type ContactMethod,
} from '../services/api';
import Header from '../components/Header';
import Footer from '../components/Footer';

// ── Helpers ───────────────────────────────────────────────────────────────

function formatCents(cents: number): string {
  return `R${(cents / 100).toFixed(0)}`;
}

// ── WhatsApp SVG icon ─────────────────────────────────────────────────────

function WhatsAppIcon() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
      <path d="M12 0C5.373 0 0 5.373 0 12c0 2.123.554 4.116 1.523 5.847L.057 23.882a.5.5 0 00.607.615l6.204-1.626A11.93 11.93 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.924 0-3.72-.506-5.27-1.39l-.378-.216-3.921 1.027 1.044-3.813-.23-.382A9.962 9.962 0 012 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z" />
    </svg>
  );
}

// ── Copy button ────────────────────────────────────────────────────────────

function CopyButton({ text, label = 'Copy link' }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    let success = false;
    try {
      await navigator.clipboard.writeText(text);
      success = true;
    } catch {
      try {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        success = document.execCommand('copy');
        document.body.removeChild(ta);
      } catch {
        // Both methods failed — don't show "Copied!" feedback
      }
    }
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="rounded-lg border border-violet-300 bg-white px-4 py-2 text-sm font-semibold text-violet-700 hover:bg-violet-50 transition-colors"
    >
      {copied ? 'Copied!' : label}
    </button>
  );
}

// ── Share section (used by hero and approved state) ────────────────────────

function ShareSection({ referralCode, referralLink }: { referralCode: string; referralLink: string }) {
  const whatsappMsg = encodeURIComponent(
    `Join My Bets and get rewarded! Sign up with my code ${referralCode}: ${referralLink}`,
  );
  const whatsappUrl = `https://wa.me/?text=${whatsappMsg}`;

  return (
    <div className="mt-4 space-y-3">
      <div>
        <p className="mb-1 text-xs font-medium text-violet-700/70">Your referral code</p>
        <span className="inline-block rounded-xl bg-violet-600 px-5 py-2 font-mono text-xl font-bold tracking-widest text-white">
          {referralCode}
        </span>
      </div>

      <div>
        <p className="mb-1 text-xs font-medium text-violet-700/70">Your referral link</p>
        <div className="flex items-center gap-2">
          <input
            readOnly
            value={referralLink}
            className="min-w-0 flex-1 rounded-lg border border-violet-200 bg-white/80 px-3 py-2 text-xs text-gray-600 outline-none"
          />
          <CopyButton text={referralLink} />
        </div>
      </div>

      <a
        href={whatsappUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="flex w-full items-center justify-center gap-2 rounded-lg bg-green-500 py-2.5 font-semibold text-white hover:bg-green-600 transition-colors"
      >
        <WhatsAppIcon />
        Share on WhatsApp
      </a>
    </div>
  );
}

// ── Contact form fields ────────────────────────────────────────────────────

function ContactFields({
  contactMethod,
  setContactMethod,
  contactDetail,
  setContactDetail,
}: {
  contactMethod: ContactMethod;
  setContactMethod: (m: ContactMethod) => void;
  contactDetail: string;
  setContactDetail: (v: string) => void;
}) {
  const placeholder =
    contactMethod === 'email'
      ? 'you@example.com'
      : contactMethod === 'call'
      ? '+27 71 234 5678'
      : '+27 71 234 5678 (WhatsApp)';

  return (
    <fieldset className="mb-4 text-left">
      <legend className="mb-2 text-sm font-semibold text-gray-700">
        Preferred contact for vetting interview
      </legend>
      <label className="mb-3 block text-sm">
        <span className="text-gray-600">Contact method</span>
        <select
          value={contactMethod}
          onChange={(e) => setContactMethod(e.target.value as ContactMethod)}
          className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
          aria-label="Contact method"
        >
          <option value="whatsapp">WhatsApp</option>
          <option value="call">Phone call</option>
          <option value="email">Email</option>
        </select>
      </label>
      <label className="block text-sm">
        <span className="text-gray-600">Contact detail</span>
        <input
          type={contactMethod === 'email' ? 'email' : 'tel'}
          required
          value={contactDetail}
          onChange={(e) => setContactDetail(e.target.value)}
          placeholder={placeholder}
          className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
          aria-label="Contact detail"
        />
      </label>
    </fieldset>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────

export default function PartnerPage() {
  const [partnerStatus, setPartnerStatus] = useState<PartnerStatusResponse | null>(null);
  const [loyalty, setLoyalty] = useState<LoyaltyResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [contactMethod, setContactMethod] = useState<ContactMethod>('whatsapp');
  const [contactDetail, setContactDetail] = useState('');

  const load = async () => {
    setError(null);
    try {
      const [partnerRes, loyaltyRes] = await Promise.all([
        getPartnerStatus(),
        getLoyalty(),
      ]);
      setPartnerStatus(partnerRes.data);
      setLoyalty(loyaltyRes.data);
      if (partnerRes.data.contact_method) {
        setContactMethod(partnerRes.data.contact_method);
      }
      if (partnerRes.data.contact_detail) {
        setContactDetail(partnerRes.data.contact_detail);
      }
    } catch {
      setError('Failed to load partner status. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleApply = async () => {
    const detail = contactDetail.trim();
    if (detail.length < 3) {
      setError('Please provide a valid contact detail (min 3 characters).');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await applyForPartner({ contact_method: contactMethod, contact_detail: detail });
      setPartnerStatus(res.data);
    } catch (err: unknown) {
      const detail_ =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(detail_ ?? 'Application failed. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen flex-col">
        <Header />
        <main className="flex flex-1 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-violet-200 border-t-violet-600" />
        </main>
        <Footer />
      </div>
    );
  }

  const s = partnerStatus?.status ?? 'none';
  const bettors = partnerStatus?.qualifying_bettors ?? 0;
  const target = partnerStatus?.milestone_target ?? 100;
  const pct = Math.min(100, Math.round((bettors / target) * 100));

  const referralCode = loyalty?.referral_code ?? '';
  const referralLink = loyalty?.referral_link ?? '';

  const totalEarned = (loyalty?.transactions ?? [])
    .filter((t) => t.amount_cents > 0)
    .reduce((sum, t) => sum + t.amount_cents, 0);

  const contactFields = (
    <ContactFields
      contactMethod={contactMethod}
      setContactMethod={setContactMethod}
      contactDetail={contactDetail}
      setContactDetail={setContactDetail}
    />
  );

  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="mx-auto max-w-2xl w-full px-4 py-8 sm:px-6 space-y-6">

        {/* Back link */}
        <Link to="/account" className="text-sm text-violet-600 hover:text-violet-800">
          &larr; Back to Account
        </Link>

        {/* Hero card */}
        <section className="rounded-2xl border border-violet-100 bg-violet-50 p-6">
          <h1 className="font-heading text-xl font-bold text-violet-800">
            Become a My Bets Partner
          </h1>
          <p className="mt-2 text-sm text-gray-600">
            Earn R10 for every person who signs up with your referral code and places their first
            bet. Recruit {target} qualifying bettors and earn a R500 milestone bonus.
          </p>
          {s === 'approved' && referralCode && (
            <ShareSection referralCode={referralCode} referralLink={referralLink} />
          )}
        </section>

        {/* Error alert */}
        {error && (
          <div role="alert" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
            {error}
          </div>
        )}

        {/* Progress bar — pending or approved */}
        {(s === 'pending' || s === 'approved') && (
          <section className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
            <div className="mb-1 flex items-center justify-between text-sm">
              <span className="text-gray-600">Qualifying bettors</span>
              <span className="font-bold tabular-nums text-violet-700">
                {bettors} / {target}
              </span>
            </div>
            <div
              className="h-3 w-full overflow-hidden rounded-full bg-gray-100"
              role="progressbar"
              aria-valuenow={pct}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              <div
                className="h-full rounded-full bg-gradient-to-r from-violet-600 via-pink-500 to-orange-400"
                style={{ width: `${pct}%` }}
              />
            </div>
            <p className="mt-1 text-right text-xs text-gray-400">{pct}%</p>
          </section>
        )}

        {/* None — apply form (only when not in error state so we don't show apply form when
            load failed and the real status might be pending/approved) */}
        {s === 'none' && !error && (
          <section className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
            <p className="mb-4 text-center text-sm text-gray-600">
              Apply to become a My Bets Partner and start earning commission.
            </p>
            {contactFields}
            <div className="text-center">
              <button
                type="button"
                disabled={busy}
                onClick={handleApply}
                data-testid="apply-btn"
                className="min-h-[44px] rounded-xl bg-violet-600 px-6 py-2 font-semibold text-white hover:bg-violet-700 disabled:opacity-50 transition-colors"
              >
                {busy ? 'Applying…' : 'Apply to become a Partner'}
              </button>
            </div>
          </section>
        )}

        {/* Pending */}
        {s === 'pending' && (
          <section className="rounded-2xl border border-amber-200 bg-amber-50 p-6 text-center">
            <p className="font-semibold text-amber-800">Application under review</p>
            <p className="mt-1 text-sm text-amber-700">
              We'll contact you within 2 business days.
            </p>
          </section>
        )}

        {/* Rejected — re-apply */}
        {s === 'rejected' && (
          <section className="rounded-2xl border border-red-200 bg-red-50 p-6">
            <p className="text-center font-semibold text-red-800">Application not approved</p>
            {partnerStatus?.reject_reason && (
              <p className="mt-1 mb-4 text-center text-sm text-red-700">
                Reason: {partnerStatus.reject_reason}
              </p>
            )}
            {contactFields}
            <div className="text-center">
              <button
                type="button"
                disabled={busy}
                onClick={handleApply}
                data-testid="reapply-btn"
                className="min-h-[44px] rounded-xl border border-violet-300 bg-white px-5 py-2 font-semibold text-violet-700 hover:bg-violet-50 disabled:opacity-50 transition-colors"
              >
                {busy ? 'Applying…' : 'Re-apply'}
              </button>
            </div>
          </section>
        )}

        {/* Approved */}
        {s === 'approved' && (
          <section className="rounded-2xl border border-green-200 bg-white p-6 shadow-sm space-y-5">
            {/* Partner badge */}
            <div className="flex items-center gap-3">
              <span className="rounded-full bg-green-100 px-3 py-1 text-sm font-bold text-green-700">
                Partner
              </span>
              <p className="text-sm text-gray-600">Your account is approved as a Partner.</p>
            </div>

            {/* Earnings summary */}
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-xl border border-gray-100 bg-gray-50 p-3 text-center">
                <p className="font-heading text-lg font-bold text-violet-700">{bettors}</p>
                <p className="text-xs text-gray-500">Qualifying bettors</p>
              </div>
              <div className="rounded-xl border border-gray-100 bg-gray-50 p-3 text-center">
                <p className="font-heading text-lg font-bold text-violet-700">
                  {loyalty?.pending_referrals ?? 0}
                </p>
                <p className="text-xs text-gray-500">Pending</p>
              </div>
              <div className="rounded-xl border border-gray-100 bg-gray-50 p-3 text-center">
                <p className="font-heading text-lg font-bold text-violet-700">
                  {formatCents(totalEarned)}
                </p>
                <p className="text-xs text-gray-500">Total earned</p>
              </div>
            </div>

            {/* Instructions */}
            <div className="rounded-xl border border-violet-100 bg-violet-50 p-4">
              <p className="text-sm font-semibold text-violet-800 mb-1">How to maximise earnings</p>
              <p className="text-sm text-gray-600">
                Share your link on social media, WhatsApp groups, and in-person. You earn R10 each
                time someone signs up and places their first bet. Reach {target} qualifying bettors
                for a R500 bonus.
              </p>
            </div>

            {/* Link to wallet */}
            <div className="text-center">
              <Link
                to="/wallet"
                className="text-sm font-semibold text-violet-600 hover:text-violet-800"
              >
                View wallet &amp; withdraw earnings &#8594;
              </Link>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
}
